import styled, { keyframes } from 'styled-components';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import {
  uFlexCenter,
  uFlexColumn,
  uHide,
  uOutlineNone,
} from '../../../assets/styles/utility';
import { variables } from '../../../assets/styles/variables';
import { SelectInput } from './SelectComponent';

export const InputWrap = styled.div`
  ${uFlexColumn};
  position: relative;
  ${({ flex }) => flex && `flex:${pxToRem(flex)}`};
`;

export const InputHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${pxToRem(4)};
`;

export const InputLabel = styled.label`
  color: ${themeColors.colorLabel};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.75;
`;

export const InputLink = styled.div`
  a,
  span {
    color: ${themeColors.colorTextSecondary};
    font-size: ${pxToRem(14)};
    letter-spacing: 0;
    line-height: 1.15;
    text-decoration: underline;
    cursor: pointer;
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    a,
    span {
      font-size: ${pxToRemMd(14)};
    }
  }
`;

export const InputError = styled.div`
  ${uFlexColumn};
  color: ${themeColors.colorError};
  font-size: ${pxToRem(14)};
  line-height: 1.35;
  font-weight: 500;
  margin: ${pxToRem(4)} 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const IconWrap = styled.div`
  ${uFlexCenter}
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};

  svg {
    width: ${pxToRem(24)};
    height: ${pxToRem(24)};
  }
`;

export const SearchIconWrap = styled(IconWrap)`
  margin-right: ${pxToRem(12)};
`;

export const CapsLocksIconWrap = styled(IconWrap)`
  margin-right: ${pxToRem(40)};
`;

export const PasswordIconWrap = styled(IconWrap)`
  margin-right: ${pxToRem(12)};
`;

export const InputFieldWrap = styled.div`
  width: 100%;
  position: relative;
`;

/**
 * No-op animations used only as a signal: browsers restart the matching
 * animation when the `:autofill`/`:-webkit-autofill` pseudo-class starts or
 * stops applying, so an `onAnimationStart` listener can detect autofill
 * (e.g. to exempt it from human-timing bot checks) without visual side effects.
 * The two keyframes must produce different CSS (a custom property here, which
 * has no visual effect) so styled-components hashes them to different
 * animation names — identical bodies would collide onto the same name and
 * make every mount look like an autofill.
 */
export const autoFillStartAnimation = keyframes`from { --autofill-signal: 1; }`;
export const autoFillCancelAnimation = keyframes`from { --autofill-signal: 0; }`;

export const InputField = styled.input`
  ${uOutlineNone};
  border: ${({ isError }) =>
    isError
      ? `1px solid ${themeColors.colorError}`
      : `1px solid ${themeColors.colorInputBorder}`};
  border-radius: ${variables.borderRadius.borderRadius};
  font-size: ${pxToRem(16)};
  line-height: 1.75;
  height: ${pxToRem(50)};
  padding: 0 ${pxToRem(12)};
  color: ${({ color }) =>
    color ? `${color}` : `${themeColors.colorInputPlaceholder}`};
  background-color: ${themeColors.colorInputBackground};
  width: 100%;
  text-align: ${({ centerText }) => (centerText ? 'center' : 'left')};

  animation-name: ${autoFillCancelAnimation};
  animation-duration: 1ms;

  &:is(:autofill, :-webkit-autofill) {
    animation-name: ${autoFillStartAnimation};
  }

  ${(props) =>
    props.type === 'number' &&
    `
    font-weight: 600;
  `};

  &:disabled {
    background-color: ${themeColors.colorInputBackgroundDisabled};
  }

  &:focus {
    border-color: ${({ isError }) =>
      isError ? themeColors.colorError : themeColors.colorInputBorderFocused};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: ${pxToRemMd(16)};
  }
`;

export const InputHidden = styled.div`
  ${uHide};
`;

export const InputFieldWrapper = styled.div`
  ${InputField} {
    ${uOutlineNone};
    border: 1px solid
      ${({ isError }) =>
        isError ? themeColors.colorError : themeColors.colorInputBorder};
    border-radius: ${variables.borderRadius.borderRadius};
    font-size: ${pxToRem(14)};
    font-weight: 400;
    line-height: 1.4;
    height: 38px;
    padding: 0 ${pxToRem(16)};
    color: ${({ color }) =>
      color ? `${color}` : `${themeColors.colorTextPrimary}`};
    background-color: ${themeColors.colorInputBg};
    width: 100%;
    text-align: ${({ centerText }) => (centerText ? 'center' : 'left')};

    ${(props) =>
      props.type === 'number' &&
      `
    font-weight: 600;
  `};

    &:disabled {
      background-color: ${themeColors.colorInputBackgroundDisabled};
    }

    &:focus {
      border-color: ${({ isError }) =>
        isError ? themeColors.colorError : themeColors.colorInputBorderFocused};
    }

    &::placeholder {
      font-size: ${pxToRem(14)};
      color: ${themeColors.colorTextSecondary};
      font-weight: 400;
    }

    ${mediaBelow(variables.breakpoints.bpXl)} {
      font-size: ${pxToRemMd(14)};
    }
  }

  ${InputLabel} {
    font-size: ${pxToRem(14)};
    font-weight: 500;
    line-height: 1.4;
    margin-bottom: ${pxToRem(6)};
  }

  ${SelectInput} {
    .c-select__control {
      &.c-select__control {
        outline: none;
        border: 1px solid ${themeColors.colorInputBorder};
        border-radius: 4px;
        font-size: ${pxToRem(14)};
        line-height: 1.4;
        height: ${pxToRem(38)};
        padding: 0 0.75rem;
        color: ${themeColors.colorLabel};
        background-color: ${themeColors.colorInputBg};
        box-shadow: none;

        &:active,
        &:focus {
          outline: none;
        }

        &.c-select__control--is-focused {
          border-color: ${themeColors.colorPrimary};
          box-shadow: none;

          .c-select__indicator {
            svg {
              transform: rotate(-180deg);
            }
          }
        }

        .css-1uccc91-singleValue {
          color: inherit;
          margin: 0;
        }

        .css-b8ldur-Input {
          margin: 0;
        }

        .c-select__value-container {
          height: 100%;
          padding: 0;
          padding-right: 2rem;
        }

        .c-select__single-value > div {
          font-size: ${pxToRem(14)};
          color: inherit;
          font-weight: 400;
        }

        .css-54k0kp-singleValue > div {
          font-size: ${pxToRem(14)};
          color: inherit;
          font-weight: 400;
        }

        .c-select__input,
        .c-select__placeholder {
          font-size: ${pxToRem(14)};
          color: ${themeColors.colorTextSecondary};
          font-weight: 400;
        }

        .c-select__indicator-separator {
          display: none;
        }

        .c-select__indicator {
          padding: 0;

          > svg {
            transition: all ease-in-out 0.3s;
            width: 1rem;
            height: 1rem;
            color: ${themeColors.colorLabel};
            transform: rotate(0);
          }
        }

        &.c-select__control--is-disabled {
          background-color: ${themeColors.colorInputBackgroundDisabled};
        }
      }
    }

    .c-select__multiple {
      .c-select__menu {
        min-height: 10rem;
      }
    }

    .c-select__menu {
      display: flex;
      flex-direction: column;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
      border: 1px solid ${themeColors.colorInputBorder};
      border-radius: 4px;
      box-shadow: 0 3px 8px 0 rgba(112, 120, 135, 0.24);
      max-height: 10rem;
      min-height: max-content;
      overflow: auto;
      z-index: 1000;

      .c-select__menu-list {
        display: flex;
        flex-direction: column;
        padding: 0;
        flex-grow: 1;
      }

      .c-select__option,
      .c-select__menu-notice {
        padding: 0.45rem 0.9375rem;
        font-size: 1rem;
        line-height: 1.75;
        letter-spacing: 0;
        color: ${themeColors.colorLabel};
        text-align: left;
        cursor: pointer;

        &:hover {
          background-color: ${themeColors.colorInputSelectItemFocused};
        }

        &.c-select__option--is-selected {
          background-color: ${themeColors.colorInputSelectItemFocused};
        }

        &.c-select__option--is-focused {
          background-color: ${themeColors.colorInputSelectItemFocused};
        }
      }
    }

    @media only screen and (max-width: 1200px - 0.02px) {
      .c-select__control {
        &.c-select__control {
          font-size: 1rem;
          min-height: 0;

          .c-select__input,
          .c-select__placeholder {
            font-size: ${pxToRem(14)};
            color: ${themeColors.colorTextSecondary};
            font-weight: 400;
          }

          .c-select__indicator {
            > svg {
              width: 1rem;
              height: 1rem;
            }
          }
        }
      }

      .c-select__menu {
        .c-select__option,
        .c-select__menu-notice {
          font-size: 1rem;
        }
      }
    }
  }
`;
